import { View, animate } from '@play-co/timestep-core/ui';
import { spineLoader } from '@play-co/timestep-spine';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import SpineAnimationView from 'src/game/components/shared/SpineAnimationView';
import { dreamDriftWearPet } from 'src/game/dreamDrift/dreamDrift.costumes';
import type { PositionType } from 'src/game/minigame/match2-timestep/lib/defs/types';
import { getPet } from 'src/game/pet';
import { createUIPet } from 'src/game/pet/pet.utils';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import sounds from 'src/lib/sounds';
import type { GameScene } from '../../minigame/match2-timestep/game/GameScene';
import { mapToViewPosition } from '../../minigame/match2-timestep/game/util/mapTools';

/** Spine export used by the Whirlwind Dancer opening presentation. */
export const whirlwindDancerPowerAnimationAsset = {
  loadMethod: spineLoader,
  url: 'assets/ui/dreamDrift/powerDancer/spine.Dreamdrift.whirlwind',
};

/** Duration used to move the travelling whirlwind between adjacent board cells. */
const WHIRLWIND_DANCER_STEP_DURATION_MS = 150;

/** Scale authored on the pet placeholder bone in the Spine setup pose. */
const WHIRLWIND_DANCER_PET_SETUP_SCALE = 0.5;

/** Callback applying the board effect when the whirlwind reaches one path cell. */
type WhirlwindDancerPathStep = (pathIndex: number) => void;

/**
 * Resolve one path cell in the same view coordinate space as puzzle blocks.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @param position Board cell occupied by the travelling whirlwind.
 * @returns Center of the cell's tile view in the puzzle layer coordinate space.
 */
function getWhirlwindDancerViewPosition(
  game: GameScene,
  position: PositionType,
): PositionType {
  // Use the spawned tile view so the whirlwind shares block positioning exactly.
  const tileView =
    game.sessionEntity.c.map.getCellAt(position)?.tile?.c.view2d.view;
  if (tileView) {
    return {
      x: tileView.x + tileView.width / 2,
      y: tileView.y + tileView.height / 2,
    };
  }

  // Fall back to the standard conversion for an unexpectedly missing tile view.
  const viewPosition = mapToViewPosition(position, true);
  return { x: viewPosition.x, y: viewPosition.y };
}

/**
 * Apply a soft shake while the whirlwind travels between two board cells.
 *
 * @param sceneRoot Root view containing the puzzle and power presentation.
 * @returns Nothing after the scene settles at its original position.
 */
async function shakeWhirlwindDancerScene(sceneRoot: View): Promise<void> {
  // Preserve the responsive scene position so each travel step settles cleanly.
  const originX = sceneRoot.style.x ?? 0;
  const originY = sceneRoot.style.y ?? 0;

  // Alternate subtle offsets for the same duration as one whirlwind movement.
  await animate(sceneRoot)
    .now({ x: originX - 3, y: originY + 2 }, 50, animate.linear)
    .then({ x: originX + 3, y: originY - 2 }, 50, animate.linear)
    .then({ x: originX, y: originY }, 50, animate.easeOut)
    .toPromise();
}

/**
 * Play the supplied Whirlwind Dancer presentation over its generated board path.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @param path Ordered board positions crossed by the travelling whirlwind.
 * @param onPathStep Callback applying board effects at each reached path index.
 * @returns Nothing after the complete presentation finishes.
 */
export async function runWhirlwindDancerPowerAnimation(
  game: GameScene,
  path: PositionType[],
  onPathStep: WhirlwindDancerPathStep,
): Promise<void> {
  // Load the supplied Spine export before changing the visible puzzle scene.
  await spineLoader(whirlwindDancerPowerAnimationAsset.url);

  // Resolve the standard Match-2 scene hierarchy from the game stage.
  const puzzleContainer = game.stage.view.getSuperview();
  const sceneRoot = puzzleContainer?.getSuperview();
  if (!puzzleContainer || !sceneRoot) return;

  // Dim the scene behind the bright puzzle grid during the hero presentation.
  const dim = new View({
    superview: sceneRoot,
    width: sceneRoot.style.width,
    height: sceneRoot.style.height,
    backgroundColor: '#000000',
    opacity: 0,
    canHandleEvents: false,
    zIndex: (puzzleContainer.style.zIndex ?? 0) - 0.5,
  });

  // Center the native 1080x1920 pet composition over the responsive scene.
  const presentationContainer = new View({
    superview: sceneRoot,
    x: sceneRoot.style.width / 2,
    y: sceneRoot.style.height / 2,
    scale: DOWNSCALE_FACTOR,
    canHandleEvents: false,
    zIndex: 100000,
  });

  // Keep the authored effects above the pet placeholder for every pet animation.
  const presentationSpine = new SpineAnimationView({
    superview: presentationContainer,
    spinePath: whirlwindDancerPowerAnimationAsset.url,
    x: 0,
    y: 0,
    zIndex: 1,
    visible: false,
  });

  // Instantiate the player's pet beneath the supplied effects composition.
  const species = getPet().species ?? 'dog';
  const pet = createUIPet('normal', species);
  pet.setAnimation(petAnimationNames[species].angry);
  dreamDriftWearPet(pet, 'whirlwindDancer');
  pet.updateOpts({
    superview: presentationContainer,
    anchorY: 300,
    offsetY: -300,
    scale: 2.4,
    zIndex: 0,
  });

  /** Keep the pet below the authored placeholder for the complete animation. */
  const updatePetWithSpine = (): void => {
    // The placeholder_pet slot is driven by the same placeholder_bear bone as Rumble.
    const bone = presentationSpine.skeleton.findBone('placeholder_bear');
    if (!bone) return;

    pet.updateOpts({
      x: bone.worldX,
      y: bone.worldY + 50,
      r: (bone.getWorldRotationX() * Math.PI) / 180,
      scaleX: bone.getWorldScaleX() * WHIRLWIND_DANCER_PET_SETUP_SCALE,
      scaleY: bone.getWorldScaleY() * WHIRLWIND_DANCER_PET_SETUP_SCALE,
    });
  };
  presentationContainer.setTick(updatePetWithSpine);

  // Place the travelling whirlwind in the Match-2 coordinate system.
  const firstPosition = path[0];
  const firstViewPosition = firstPosition
    ? getWhirlwindDancerViewPosition(game, firstPosition)
    : undefined;
  const whirlwindSpine = new SpineAnimationView({
    superview: game.view2d.layers.foreground.view,
    spinePath: whirlwindDancerPowerAnimationAsset.url,
    x: firstViewPosition?.x ?? 0,
    y: firstViewPosition?.y ?? 0,
    scale: DOWNSCALE_FACTOR * 0.7,
    zIndex: 100000,
    visible: false,
  });

  // Dim the scene before revealing the opening pet animation.
  await animate(dim).then({ opacity: 0.5 }, 300, animate.linear).toPromise();

  // Play pet_init as the export's authored entrance before starting idle effects.
  presentationSpine.show();
  const petEnter = presentationSpine.start({ id: 'pet_init', mix: 0 });
  await petEnter.complete;

  // Keep the pet effects looping during path traversal.
  presentationSpine.start({ id: 'pet_idle', loop: true, mix: 0 });

  // Start the whirlwind and its one-shot sound only for a nonempty path.
  if (firstPosition) {
    whirlwindSpine.show();
    whirlwindSpine.start({ id: 'whirlwindloop', loop: true, mix: 0 });
    sounds.sfx.play('dreamDriftTornado');
    onPathStep(0);
  }

  // Move through every remaining path cell and apply its synchronized contact.
  for (let pathIndex = 1; pathIndex < path.length; pathIndex++) {
    const viewPosition = getWhirlwindDancerViewPosition(game, path[pathIndex]);
    const movement = animate(whirlwindSpine)
      .then(
        { x: viewPosition.x, y: viewPosition.y },
        WHIRLWIND_DANCER_STEP_DURATION_MS,
        animate.linear,
      )
      .toPromise();
    const shake = shakeWhirlwindDancerScene(sceneRoot);
    await Promise.all([movement, shake]);

    // Apply board changes when the travelling whirlwind reaches this cell.
    onPathStep(pathIndex);
  }

  // Remove the board whirlwind before playing the authored pet exit.
  whirlwindSpine.state.clearTracks();
  whirlwindSpine.hide();
  const petExit = presentationSpine.start({ id: 'pet_exit', mix: 0 });
  await petExit.complete;

  // Restore the scene after the complete presentation finishes.
  await animate(dim).then({ opacity: 0 }, 300, animate.linear).toPromise();

  // Remove all temporary animation state after the presentation finishes.
  presentationContainer.setTick(null);
  presentationSpine.state.clearTracks();
  presentationSpine.state.clearListeners();
  whirlwindSpine.state.clearTracks();
  whirlwindSpine.state.clearListeners();
  whirlwindSpine.removeFromSuperview();
  presentationContainer.removeFromSuperview();
  dim.removeFromSuperview();
}
